import { useMemo, useState, useEffect } from 'react'
import useSWRMutation from 'swr/mutation'
import { swrConfig } from '@/api/base/swr-config'
import useSWR from 'swr'
import { agentService } from '@/api/services/agent.service'
import { datasetService } from '@/api/services/dataset.service'
import type {
  SearchAgentRequest,
  GetAgentByIdRequest,
  UpdateAgentRequest,
  BaseAgentRequest,
  PublishAgentRequest,
} from '@/api/types/agent.type'

/**
 * 获取首页展示智能体（平台精选 + 精选列表）
 */
export const useHomeDisplayAgents = (workspaceId: string | undefined) => {
  return useSWR(
    workspaceId ? `homeDisplayAgents:${workspaceId}` : null,
    () => agentService.getHomeDisplayAgents({ workspaceId }),
    swrConfig
  )
}

/**
 * 搜索所有智能体列表
 */
export const useSearchAllAgents = (params: SearchAgentRequest) => {
  const key = params.workspaceId
    ? `searchAllAgents:${params.workspaceId}:${params.keyWord || ''}`
    : null

  return useSWR(key, () => agentService.searchAllAgents(params), swrConfig)
}

/**
 * 搜索我的智能体列表
 */
export const useSearchMyAgents = (params: SearchAgentRequest) => {
  const key = params.workspaceId
    ? `searchMyAgents:${params.workspaceId}:${params.keyWord || ''}`
    : null

  return useSWR(key, () => agentService.searchMyAgents(params), swrConfig)
}

/**
 * 根据ID查询智能体详情
 */
export const useGetAgentById = (params: GetAgentByIdRequest) => {
  return useSWR(
    `getAgentById:${params.agentId}`,
    () => agentService.getAgentById(params.agentId),
    swrConfig
  )
}
/**
 * 根据ID查询智能体详情，草稿详情
 */
export const useGetDraftByAgentId = (params: GetAgentByIdRequest) => {
  const key = params.agentId ? `getDraftByAgentId:${params.agentId}` : null

  return useSWR(
    key,
    () => {
      if (!params.agentId) throw new Error('Agent ID is required')
      return agentService.getDraftByAgentId(params.agentId)
    },
    {
      ...swrConfig,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  )
}

/**
 * 新增智能体
 */
export const useSaveAgent = () => {
  return useSWRMutation(
    'saveAgent',
    async (_url: string, { arg }: { arg: BaseAgentRequest }) => {
      return await agentService.saveAgent(arg)
    },
    {
      revalidate: false,
      populateCache: false,
      throwOnError: false,
    }
  )
}

/**
 * 编辑智能体
 */
export const useUpdateAgent = () => {
  return useSWRMutation(
    'updateAgent',
    async (_url: string, { arg }: { arg: UpdateAgentRequest }) => {
      return await agentService.updateAgent(arg)
    },
    {
      revalidate: false,
      populateCache: false,
      throwOnError: false,
    }
  )
}

/**
 * 发布智能体
 */
export const usePublishAgent = () => {
  return useSWRMutation(
    'publishAgent',
    async (_url: string, { arg }: { arg: PublishAgentRequest }) => {
      return await agentService.publishAgent(arg)
    },
    {
      revalidate: false,
      populateCache: false,
      throwOnError: false,
    }
  )
}

/**
 * 智能体相关钩子组合
 */
export const useAgent = () => {
  return {
    // 查询相关
    useSearchAllAgents,
    useSearchMyAgents,
    useGetAgentById,
    useGetDraftByAgentId,

    // 操作相关
    useSaveAgent,
    useUpdateAgent,
    usePublishAgent,
  }
}

/**
 * 获取智能体详情的Hook
 * @param id 智能体ID
 * @returns 智能体详情数据
 */
export const useAgentDetail = (id: string | undefined) => {
  const key = id ? ['agent', 'detail', id] : null

  return useSWR(
    key,
    () => {
      if (!id) throw new Error('Agent ID is required')
      return agentService.getAgentById(id)
    },
    {
      ...swrConfig,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  )
}

/**
 * 智能体知识库配置接口
 */
export interface AgentSearchConfig {
  db_name: string
  milvus_config: Array<{
    collection: string
    part: string
  }>
}

/**
 * 获取智能体知识库配置的Hook
 * @param agentDetail 智能体详情（从外部传入）
 * @returns 知识库配置和相关状态
 */
export const useAgentDetailWithSearchConfig = (agentDetail: any) => {
  const [datasetResults, setDatasetResults] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<any>(null)

  // 获取所有知识库的数据集列表
  useEffect(() => {
    const abortController = new AbortController()

    const fetchDatasets = async () => {
      if (!agentDetail?.knowledgeBaseIdList) {
        setDatasetResults([])
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const results = await Promise.all(
          agentDetail.knowledgeBaseIdList.map(async (knowledgeId: number) => {
            const data = await datasetService.getDatasetList(
              {
                knowledgeBaseId: knowledgeId.toString(),
              },
              abortController.signal
            )
            return { knowledgeId, data }
          })
        )

        // 检查是否已被取消
        if (!abortController.signal.aborted) {
          setDatasetResults(results)
        }
      } catch (err) {
        if (!abortController.signal.aborted) {
          setError(err)
          console.error('获取数据集失败:', err)
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false)
        }
      }
    }

    fetchDatasets()

    // 清理函数：取消未完成的请求
    return () => {
      abortController.abort()
    }
  }, [agentDetail?.knowledgeBaseIdList])

  // 构建搜索配置
  const searchConfig = useMemo(() => {
    if (!agentDetail || isLoading || error) {
      return null
    }

    const { workspaceId } = agentDetail

    // 构建 milvus_config
    const milvusConfig = datasetResults.flatMap(
      ({ knowledgeId, data }: { knowledgeId: number; data: any }) => {
        if (!data?.datasets) return []

        return data.datasets.map((dataset: any) => ({
          collection: `knowledge_${knowledgeId}`,
          part: `dataset_${dataset.id}`,
        }))
      }
    )

    return {
      db_name: `workspace_${workspaceId}`,
      milvus_config: milvusConfig,
    } as AgentSearchConfig
  }, [agentDetail, datasetResults, isLoading, error])

  return {
    // 智能体详情
    agentDetail,

    // 数据集信息
    datasetResults,

    // 搜索配置
    searchConfig,

    // 综合状态
    isLoading,
    error,
  }
}
